import { useState, useMemo, useRef } from 'react';
import { useBestFitContextMenu } from './useBestFitContextMenu';

const fmtK = (n: number): string => {
  if (!n) return '';
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

interface DoneDetail {
  price: number;
  bLotPerFreq: number;
  bFreq: number;
  bLot: number;
  sLot: number;
  sFreq: number;
  sLotPerFreq: number;
  freq: number;
  lot: number;
}

export function DoneDetails({ currentTime, allOrders, prevClose }: { currentTime: number; allOrders: any[]; prevClose?: number | null }) {
  const [sortConfig, setSortConfig] = useState<{ key: keyof DoneDetail; direction: 'asc' | 'desc' } | null>(null);

  // Refs for horizontal scroll sync between main table and total row
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const totalDivRef = useRef<HTMLDivElement>(null);
  const syncTotalScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (totalDivRef.current) totalDivRef.current.scrollLeft = e.currentTarget.scrollLeft;
  };

  // Use a ref to store incremental calculation state so we don't recreate maps and loop from 0
  const stateRef = useRef<{
    lastTime: number; lastIndex: number; lastOrders: any[] | null;
    priceMap: Map<number, { bFreq: number; bLot: number; sLot: number; sFreq: number; freq: number; lot: number }>;
  }>({
    lastTime: -1, lastIndex: 0, lastOrders: null, priceMap: new Map(),
  });

  const doneDetails = useMemo(() => {
    if (!allOrders.length) {
      stateRef.current = { lastTime: -1, lastIndex: 0, lastOrders: allOrders, priceMap: new Map() };
      return [];
    }

    // Reset when a new dataset is loaded
    if (stateRef.current.lastOrders !== allOrders) {
      stateRef.current = { lastTime: -1, lastIndex: 0, lastOrders: allOrders, priceMap: new Map() };
    }

    const state = stateRef.current;

    // If time goes backwards (scrubbing), reset state to recalculate from 0
    if (currentTime < state.lastTime) {
      state.lastIndex = 0;
      state.priceMap.clear();
    }

    const priceMap = state.priceMap;

    let i = state.lastIndex;
    for (; i < allOrders.length; i++) {
      const order = allOrders[i];
      if (order.timeValue > currentTime) {
        break;
      }

      // allOrders is now tradeOrders (which comes from done details)
      // They are all done trades by definition. We use order.lot for volume.
      const isTrade = order.lot > 0;

      if (isTrade) {
        const price = order.price;
        const volume = order.lot;

        let existing = priceMap.get(price);
        if (!existing) {
          existing = { bFreq: 0, bLot: 0, sLot: 0, sFreq: 0, freq: 0, lot: 0 };
          priceMap.set(price, existing);
        }

        existing.freq += 1;
        existing.lot += volume;

        if (order.buySell === 'B') {
          existing.sFreq += 1;
          existing.sLot += volume;
        } else {
          existing.bFreq += 1;
          existing.bLot += volume;
        }
      }
    }

    // Save incremental progress
    state.lastIndex = i;
    state.lastTime = currentTime;

    const data: DoneDetail[] = Array.from(priceMap.entries()).map(([price, stats]) => ({
      price,
      bLotPerFreq: stats.bFreq > 0 ? Math.round(stats.bLot / stats.bFreq) : 0,
      bFreq: stats.bFreq,
      bLot: Math.round(stats.bLot),
      sLot: Math.round(stats.sLot),
      sFreq: stats.sFreq,
      sLotPerFreq: stats.sFreq > 0 ? Math.round(stats.sLot / stats.sFreq) : 0,
      freq: stats.freq,
      lot: Math.round(stats.lot)
    }));

    if (sortConfig) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default: Sort by price descending
      data.sort((a, b) => b.price - a.price);
    }
    return data;
  }, [currentTime, allOrders, sortConfig]);

  const handleSort = (key: keyof DoneDetail) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const totals = useMemo(() => ({
    bFreq: doneDetails.reduce((s, r) => s + r.bFreq, 0),
    bLot:  doneDetails.reduce((s, r) => s + r.bLot,  0),
    sLot:  doneDetails.reduce((s, r) => s + r.sLot,  0),
    sFreq: doneDetails.reduce((s, r) => s + r.sFreq, 0),
    freq:  doneDetails.reduce((s, r) => s + r.freq,  0),
    lot:   doneDetails.reduce((s, r) => s + r.lot,   0),
  }), [doneDetails]);

  const { isBestFit, handleContextMenu, ContextMenu } = useBestFitContextMenu();

  const colWidths = useMemo(() => {
    let maxPrice = 5, maxBLotPF = 4, maxBFreq = 4, maxBLot = 4, maxSLot = 4, maxSFreq = 4, maxSLotPF = 4, maxFreq = 4, maxLot = 3;
    if (isBestFit) {
      for (const d of doneDetails) {
        maxPrice = Math.max(maxPrice, String(d.price.toLocaleString()).length);
        maxBLotPF = Math.max(maxBLotPF, String(d.bLotPerFreq ? d.bLotPerFreq.toLocaleString() : '').length);
        maxBFreq = Math.max(maxBFreq, String(d.bFreq.toLocaleString()).length);
        maxBLot = Math.max(maxBLot, String(d.bLot.toLocaleString()).length);
        maxSLot = Math.max(maxSLot, String(d.sLot.toLocaleString()).length);
        maxSFreq = Math.max(maxSFreq, String(d.sFreq.toLocaleString()).length);
        maxSLotPF = Math.max(maxSLotPF, String(d.sLotPerFreq ? d.sLotPerFreq.toLocaleString() : '').length);
        maxFreq = Math.max(maxFreq, String(d.freq.toLocaleString()).length);
        maxLot = Math.max(maxLot, String(d.lot.toLocaleString()).length);
      }
      const tbFreq = totals.bFreq > 0 ? Math.round(totals.bLot / totals.bFreq).toLocaleString() : '';
      const tsFreq = totals.sFreq > 0 ? Math.round(totals.sLot / totals.sFreq).toLocaleString() : '';
      maxBLotPF = Math.max(maxBLotPF, tbFreq.length);
      maxBFreq = Math.max(maxBFreq, fmtK(totals.bFreq).length);
      maxBLot = Math.max(maxBLot, fmtK(totals.bLot).length);
      maxSLot = Math.max(maxSLot, fmtK(totals.sLot).length);
      maxSFreq = Math.max(maxSFreq, fmtK(totals.sFreq).length);
      maxSLotPF = Math.max(maxSLotPF, tsFreq.length);
      maxFreq = Math.max(maxFreq, fmtK(totals.freq).length);
      maxLot = Math.max(maxLot, fmtK(totals.lot).length);
    }
    const px = (n: number) => isBestFit ? `${Math.ceil(n * 10.0) + 24}px` : undefined;
    return {
      price: px(maxPrice), bLotPF: px(maxBLotPF), bFreq: px(maxBFreq), bLot: px(maxBLot),
      sLot: px(maxSLot), sFreq: px(maxSFreq), sLotPF: px(maxSLotPF), freq: px(maxFreq), lot: px(maxLot),
    };
  }, [doneDetails, totals, isBestFit]);

  return (
    <div className="flex flex-col h-full bg-black border-t border-gray-800 relative">
      <ContextMenu />
      {/* Main scroll area */}
      <div
        ref={mainScrollRef}
        className={`flex-1 overflow-y-auto ${isBestFit ? 'overflow-x-auto' : 'overflow-x-hidden'}`}
        onScroll={syncTotalScroll}
        style={{ willChange: 'transform' }}
      >
        <table className={`${isBestFit ? '' : 'w-full table-fixed'} text-[14.5px] tabular-nums`}>
          <thead className="sticky top-0 bg-gray-900 text-gray-400 font-bold z-10" style={{ transform: 'translateZ(0)' }} onContextMenu={handleContextMenu}>
            <tr className="cursor-pointer select-none">
              <th onClick={() => handleSort('price')} className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[10%]' : ''}`} style={isBestFit ? {width: colWidths.price} : undefined}>Price</th>
              <th onClick={() => handleSort('bLotPerFreq')} className={`px-0 py-0.5 text-center border-b border-gray-700 text-[12px] ${!isBestFit ? 'w-[6%]' : ''}`} style={isBestFit ? {width: colWidths.bLotPF} : undefined}></th>
              <th onClick={() => handleSort('bFreq')} className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.bFreq} : undefined}>B Freq</th>
              <th onClick={() => handleSort('bLot')} className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[15%]' : ''}`} style={isBestFit ? {width: colWidths.bLot} : undefined}>B Lot</th>
              <th onClick={() => handleSort('sLot')} className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[15%]' : ''}`} style={isBestFit ? {width: colWidths.sLot} : undefined}>S Lot</th>
              <th onClick={() => handleSort('sFreq')} className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.sFreq} : undefined}>S Freq</th>
              <th onClick={() => handleSort('sLotPerFreq')} className={`px-0 py-0.5 text-center border-b border-gray-700 text-[12px] ${!isBestFit ? 'w-[6%]' : ''}`} style={isBestFit ? {width: colWidths.sLotPF} : undefined}></th>
              <th onClick={() => handleSort('freq')} className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.freq} : undefined}>Freq</th>
              <th onClick={() => handleSort('lot')} className={`pr-3 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[15%]' : ''}`} style={isBestFit ? {width: colWidths.lot} : undefined}>Lot</th>
            </tr>
          </thead>
          <tbody>
            {doneDetails.map((detail, idx) => {
              let priceClass = "text-gray-300";
              if (prevClose) {
                if (detail.price > prevClose) priceClass = "text-green-500";
                else if (detail.price < prevClose) priceClass = "text-red-500";
                else priceClass = "text-yellow-500";
              }

              let bFreqClass = "text-gray-400";
              let sFreqClass = "text-gray-400";
              if (detail.bFreq > detail.sFreq) {
                bFreqClass = "text-green-500";
                sFreqClass = "text-red-500";
              } else if (detail.bFreq < detail.sFreq) {
                bFreqClass = "text-red-500";
                sFreqClass = "text-green-500";
              }

              return (
                <tr key={idx} className="hover:bg-gray-900 h-[22.5px]">
                  <td className={`px-0 py-0 text-right truncate ${priceClass}`} style={isBestFit ? {width: colWidths.price} : undefined}>{detail.price.toLocaleString()}</td>
                  <td className="px-0 py-0 text-right text-gray-500 text-[12px] pr-1" style={isBestFit ? {width: colWidths.bLotPF} : undefined}>{detail.bLotPerFreq ? detail.bLotPerFreq.toLocaleString() : ''}</td>
                  <td className={`px-0 py-0 text-right ${bFreqClass}`} style={isBestFit ? {width: colWidths.bFreq} : undefined}>{detail.bFreq.toLocaleString()}</td>
                  <td className="px-0 py-0 text-right text-white" style={isBestFit ? {width: colWidths.bLot} : undefined}>{detail.bLot.toLocaleString()}</td>
                  <td className="px-0 py-0 text-right text-white" style={isBestFit ? {width: colWidths.sLot} : undefined}>{detail.sLot.toLocaleString()}</td>
                  <td className={`px-0 py-0 text-right ${sFreqClass}`} style={isBestFit ? {width: colWidths.sFreq} : undefined}>{detail.sFreq.toLocaleString()}</td>
                  <td className="px-0 py-0 text-right text-gray-500 text-[12px] pr-1" style={isBestFit ? {width: colWidths.sLotPF} : undefined}>{detail.sLotPerFreq ? detail.sLotPerFreq.toLocaleString() : ''}</td>
                  <td className="px-0 py-0 text-right text-gray-400" style={isBestFit ? {width: colWidths.freq} : undefined}>{detail.freq.toLocaleString()}</td>
                  <td className="pr-3 py-0 text-right text-gray-400" style={isBestFit ? {width: colWidths.lot} : undefined}>{detail.lot.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Total row — always pinned to the bottom of the panel (shrink-0).
          In best-fit mode, scrollLeft is synced from the main div via syncTotalScroll
          so columns stay aligned even when scrolled horizontally. */}
      {doneDetails.length > 0 && (
        <div
          ref={totalDivRef}
          className="shrink-0 overflow-x-hidden border-t border-gray-700 bg-gray-900"
        >
          <table className={`${isBestFit ? '' : 'w-full table-fixed'} text-[14.5px] tabular-nums font-bold`}>
            <tbody>
              <tr>
                <td className={`px-0 py-0.5 text-center text-gray-500 text-[12px] ${!isBestFit ? 'w-[10%]' : ''}`} style={isBestFit ? {width: colWidths.price} : undefined}>TOTAL</td>
                <td className={`px-0 py-0.5 text-right text-gray-500 text-[12px] pr-1 ${!isBestFit ? 'w-[6%]' : ''}`} style={isBestFit ? {width: colWidths.bLotPF} : undefined}>{totals.bFreq > 0 ? Math.round(totals.bLot / totals.bFreq).toLocaleString() : ''}</td>
                <td className={`px-0 py-0.5 text-right text-green-500 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.bFreq} : undefined}>{fmtK(totals.bFreq)}</td>
                <td className={`px-0 py-0.5 text-right text-green-500 ${!isBestFit ? 'w-[15%]' : ''}`} style={isBestFit ? {width: colWidths.bLot} : undefined}>{fmtK(totals.bLot)}</td>
                <td className={`px-0 py-0.5 text-right text-red-500 ${!isBestFit ? 'w-[15%]' : ''}`} style={isBestFit ? {width: colWidths.sLot} : undefined}>{fmtK(totals.sLot)}</td>
                <td className={`px-0 py-0.5 text-right text-red-500 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.sFreq} : undefined}>{fmtK(totals.sFreq)}</td>
                <td className={`px-0 py-0.5 text-right text-gray-500 text-[12px] pr-1 ${!isBestFit ? 'w-[6%]' : ''}`} style={isBestFit ? {width: colWidths.sLotPF} : undefined}>{totals.sFreq > 0 ? Math.round(totals.sLot / totals.sFreq).toLocaleString() : ''}</td>
                <td className={`px-0 py-0.5 text-right text-white ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.freq} : undefined}>{fmtK(totals.freq)}</td>
                <td className={`pr-3 py-0.5 text-right text-white ${!isBestFit ? 'w-[15%]' : ''}`} style={isBestFit ? {width: colWidths.lot} : undefined}>{fmtK(totals.lot)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
